import { useAuth } from '@/auth/useAuth';
import { usePendientesVerificacion } from '@/hooks';
import { rutaListaEmpresas } from '@/utils/empresasFiltros';
import {
  Activity,
  Award,
  BarChart3,
  Building2,
  ChevronLeft,
  ClipboardCheck,
  Copy,
  Download,
  FileWarning,
  LayoutDashboard,
  ListChecks,
  Menu,
  PlayCircle,
  Users,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import './admin.css';

// ─── Types ──────────────────────────────────────────────

export type AdminView =
  | 'dashboard'
  | 'asesores'
  | 'asesores-detail'
  | 'empresas'
  | 'empresas-detail'
  | 'en-curso'
  | 'revision'
  | 'revision-detail'
  | 'indicadores'
  | 'matrices'
  | 'reportes'
  | 'normas'
  | 'issues-pendientes'
  | 'issues-audios'
  | 'issues-duplicados';

interface NavItem {
  id: AdminView;
  label: string;
  icon: ReactNode;
  badge?: number | null;
  badgeType?: 'red' | 'amber';
}

interface NavSection {
  title: string;
  items: NavItem[];
}

// ─── Route mappings ─────────────────────────────────────

const VIEW_TO_PATH: Record<string, string> = {
  dashboard: '/dashboard',
  asesores: '/asesores',
  empresas: '/empresas',
  'en-curso': '/en-curso',
  revision: '/revision',
  indicadores: '/indicadores',
  matrices: '/matrices',
  reportes: '/reportes',
  normas: '/normas',
  'issues-pendientes': '/hallazgos/actas',
  'issues-audios': '/hallazgos/audios-de-otro-asesor',
  'issues-duplicados': '/hallazgos/audios-duplicados',
};

function resolveView(pathname: string): AdminView {
  if (pathname === '/dashboard' || pathname === '/') return 'dashboard';
  if (/^\/asesores\/[^/]+/.test(pathname)) return 'asesores-detail';
  if (pathname === '/asesores') return 'asesores';
  if (/^\/empresas\/[^/]+/.test(pathname)) return 'empresas-detail';
  if (pathname === '/empresas') return 'empresas';
  if (pathname === '/en-curso') return 'en-curso';
  if (/^\/revision\/[^/]+/.test(pathname)) return 'revision-detail';
  if (pathname === '/revision') return 'revision';
  if (pathname.startsWith('/indicadores')) return 'indicadores';
  if (pathname === '/matrices') return 'matrices';
  if (pathname === '/reportes') return 'reportes';
  if (pathname === '/normas' || pathname === '/admin/normas' || /^\/normas\/[^/]+/.test(pathname)) return 'normas';
  if (pathname === '/hallazgos/actas') return 'issues-pendientes';
  if (pathname === '/hallazgos/audios-de-otro-asesor') return 'issues-audios';
  if (pathname === '/hallazgos/audios-duplicados') return 'issues-duplicados';
  return 'dashboard';
}

/** Which sidebar item should be highlighted for a given view */
function sidebarHighlight(view: AdminView): AdminView {
  if (view === 'asesores-detail') return 'asesores';
  if (view === 'empresas-detail') return 'empresas';
  if (view === 'revision-detail') return 'revision';
  return view;
}

const VIEW_TITLES: Record<AdminView, string> = {
  dashboard: 'Dashboard general',
  asesores: 'Asesores',
  'asesores-detail': 'Detalle de asesor',
  empresas: 'Empresas',
  'empresas-detail': 'Detalle de empresa',
  'en-curso': 'Asesorías en curso',
  revision: 'Asesorías en revisión',
  'revision-detail': 'Revisión de asesoría',
  indicadores: 'Indicadores de gestión',
  matrices: 'Matrices NTC globales',
  reportes: 'Reportes y Exportación',
  normas: 'Detalle por Normas',
  'issues-pendientes': 'Actas con hallazgos',
  'issues-audios': 'Audios de otro asesor',
  'issues-duplicados': 'Audios duplicados',
};

// ─── Component ──────────────────────────────────────────

interface AdminLayoutProps {
  children?: ReactNode;
}

export function AdminLayout(_props: AdminLayoutProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  // Pending count for badge
  const { pendientes } = usePendientesVerificacion();
  const pendientesCount = pendientes.length;

  // Resolve current view from pathname (supports dynamic segments)
  const currentView: AdminView = resolveView(location.pathname);
  const highlightedView = sidebarHighlight(currentView);

  const handleNav = (view: AdminView) => {
    const path = view === 'empresas' ? rutaListaEmpresas() : VIEW_TO_PATH[view];
    if (path) navigate(path);
    setSidebarOpen(false);
  };

  const sections: NavSection[] = [
    {
      title: 'General',
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-3.5 h-3.5" /> },
        { id: 'asesores', label: 'Asesores', icon: <Users className="w-3.5 h-3.5" /> },
        { id: 'empresas', label: 'Empresas', icon: <Building2 className="w-3.5 h-3.5" /> },
      ],
    },
    {
      title: 'Asesorías',
      items: [
        { id: 'en-curso', label: 'En curso', icon: <PlayCircle className="w-3.5 h-3.5" /> },
        {
          id: 'revision',
          label: 'En revisión',
          icon: <ClipboardCheck className="w-3.5 h-3.5" />,
          badge: pendientesCount > 0 ? pendientesCount : null,
          badgeType: 'red',
        },
      ],
    },
    {
      title: 'Análisis',
      items: [
        { id: 'indicadores', label: 'Indicadores', icon: <Activity className="w-3.5 h-3.5" /> },
        { id: 'matrices', label: 'Matrices NTC', icon: <BarChart3 className="w-3.5 h-3.5" /> },
        { id: 'reportes', label: 'Reportes', icon: <Download className="w-3.5 h-3.5" /> },
        { id: 'normas', label: 'Por Normas', icon: <Award className="w-3.5 h-3.5" /> },
      ],
    },
    // Solo para correos guardados con "+" (revisión de hallazgos).
    ...(user?.asesor?.gestionIssues
      ? [
          {
            title: 'Revisión de hallazgos',
            items: [
              { id: 'issues-pendientes' as const, label: 'Actas', icon: <ListChecks className="w-3.5 h-3.5" /> },
              { id: 'issues-audios' as const, label: 'Audios de otro asesor', icon: <FileWarning className="w-3.5 h-3.5" /> },
              { id: 'issues-duplicados' as const, label: 'Audios duplicados', icon: <Copy className="w-3.5 h-3.5" /> },
            ],
          },
        ]
      : []),
  ];

  const now = new Date();
  const monthYear = now.toLocaleString('es-CO', { month: 'long', year: 'numeric' });

  return (
    <div className="admin-shell" style={{ '--admin-offset': 'calc(var(--banner-height, 0px) + var(--header-height, 64.5px))' } as any}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed left-0 right-0 bottom-0 bg-black/30 z-40 lg:hidden"
          style={{ top: 'var(--admin-offset)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <nav className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-logo">
          <div className="text-[15px] font-semibold tracking-tight text-(--admin-text)">NTC Admin</div>
          <div className="text-[11px] text-(--admin-text3) mt-0.5">Panel de gestión</div>
        </div>

        {sections.map((section) => (
          <div key={section.title}>
            <div className="admin-nav-section">{section.title}</div>
            {section.items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`admin-nav-item w-full ${highlightedView === item.id ? 'active' : ''}`}
                onClick={() => handleNav(item.id)}
              >
                {item.icon}
                <span>{item.label}</span>
                {item.badge != null && item.badge > 0 && (
                  <span className={item.badgeType === 'amber' ? 'admin-badge-amber' : 'admin-badge'}>{item.badge}</span>
                )}
              </button>
            ))}
          </div>
        ))}
      </nav>

      {/* Main */}
      <div className="admin-main">
        {/* Top bar */}
        <div className="admin-top-bar z-100">
          <button
            type="button"
            className="lg:hidden p-1.5 rounded-lg hover:bg-(--admin-surface2) transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5 text-(--admin-text2)" />
          </button>

          {currentView !== 'dashboard' &&
            (() => {
              const isDetail = currentView === 'asesores-detail' || currentView === 'empresas-detail' || currentView === 'revision-detail';
              const backView = isDetail ? sidebarHighlight(currentView) : 'dashboard';
              const backLabel = isDetail ? VIEW_TITLES[backView] : 'Dashboard';
              return (
                <button
                  type="button"
                  onClick={() => handleNav(backView)}
                  className="flex items-center gap-1 text-[12px] text-(--admin-text2) hover:text-(--admin-text) transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  {backLabel}
                </button>
              );
            })()}

          <div className="text-[15px] font-semibold text-(--admin-text)">{VIEW_TITLES[currentView]}</div>

          <div className="text-[12px] text-(--admin-text3) ml-auto capitalize">Actualizado hoy · {monthYear}</div>
        </div>

        {/* Content */}
        <div className="admin-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
